import React, { createContext, useState, useEffect, ReactNode } from 'react';
import { jwtDecode } from 'jwt-decode';
import {
  AuthService,
  AuthTokens,
  isNewPasswordChallenge,
} from '../services/AuthService';
import { resolveAdminRole } from '../utils/resolveAdminRole';
import {
  clearTokens,
  getRefreshToken,
  purgeLegacyAccessToken,
  setAccessToken,
  setRefreshToken,
} from '../services/tokenStore';

interface User {
  email: string;
  role: string | null;
}

export type LoginResult =
  | { status: 'ok' }
  | {
      status: 'newPasswordRequired';
      complete: (newPassword: string) => Promise<void>;
    };

interface AuthState {
  isAuthenticated: boolean;
  user: User | null;
  token: string | null;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<LoginResult>;
  logout: () => void;
}

interface DecodedToken {
  'cognito:groups'?: string[];
  email: string;
  exp: number;
}

export const AuthContext = createContext<AuthState | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // The access token is no longer persisted (see tokenStore), so a reload starts
  // with nothing in memory. Exchange the stored refresh token for a fresh access
  // token instead of reading a long-lived credential back out of localStorage.
  useEffect(() => {
    let cancelled = false;

    const restoreSession = async () => {
      // Remove any access token written by an older build of the panel.
      purgeLegacyAccessToken();

      const refreshToken = getRefreshToken();
      if (!refreshToken) {
        if (!cancelled) setIsLoading(false);
        return;
      }

      try {
        const refreshed = await AuthService.refreshSession(refreshToken);
        if (cancelled) return;
        applySession(refreshed);
      } catch (error) {
        console.error('Could not restore session:', error);
        if (!cancelled) {
          clearTokens();
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    void restoreSession();

    return () => {
      cancelled = true;
    };
    // applySession is stable for the lifetime of the provider.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applySession = (tokens: AuthTokens) => {
    // Access token: memory only. Refresh token: localStorage, because the
    // session must survive a reload and there is no cookie path yet.
    setAccessToken(tokens.accessToken);
    if (tokens.refreshToken) {
      setRefreshToken(tokens.refreshToken);
    }

    const decodedToken = jwtDecode<DecodedToken>(tokens.accessToken);
    const userRole = resolveAdminRole(decodedToken['cognito:groups']);
    setToken(tokens.accessToken);
    setUser({ email: decodedToken.email, role: userRole });
    setIsAuthenticated(true);
  };

  const applyTokens = applySession;

  const login = async (
    email: string,
    password: string
  ): Promise<LoginResult> => {
    const result = await AuthService.signIn(email, password);

    if (isNewPasswordChallenge(result)) {
      return {
        status: 'newPasswordRequired',
        complete: async (newPassword: string) => {
          const tokens = await AuthService.completeNewPasswordChallenge(
            result.cognitoUser,
            newPassword,
            result.userAttributes
          );
          applyTokens(tokens);
        },
      };
    }

    applyTokens(result);
    return { status: 'ok' };
  };

  const logout = () => {
    AuthService.signOut();
    clearTokens();
    setToken(null);
    setUser(null);
    setIsAuthenticated(false);
  };

  return (
    <AuthContext.Provider
      value={{ isAuthenticated, user, token, isLoading, login, logout }}
    >
      {children}
    </AuthContext.Provider>
  );
};
